import { useMemo } from 'react'
import { Stack, StackProps, Typography } from '@mui/material'
import { DashBoardConfigure, DashBoardItemData, GraphOptions } from '../../../types'
import { TargetListResult } from "interface/search/targetList"
import { getAxisTitle, getValueTitle } from 'features/search/targetList/util'

export const useDashBoardItemTitles = <T,>({
	configure,
	targetList,
	itemData,
	globalAxisKey,
	options
}: {
  configure: DashBoardConfigure<T>,
	targetList?: TargetListResult
	itemData: DashBoardItemData,
	globalAxisKey?: string,
	options?: GraphOptions[][]
}) => useMemo<{
	text: string
	def?: GraphOptions
}[]>(
	() => {
		if (itemData.title) return [{ text: itemData.title }]

		return options?.flatMap(
			(line) => line.flatMap(
				(optionDef) => {
					if (optionDef.showOnTitle === false) return []

					const option = itemData.options?.find((opt) => opt.id === optionDef.id)
					if (!option) return []

					if (optionDef.type === 'axis' || optionDef.type === 'rank' || optionDef.type === 'time') {
						const key = (option.value === "@global") ? globalAxisKey : option.value
						if (key) {
							return {
								def: optionDef,
								text: getAxisTitle(configure, key, targetList) ?? '(無効)'
							}
						}
					} else if (optionDef.type === 'value') {
						const key = option.value
						if (key) {
							return {
								def: optionDef,
								text: getValueTitle(configure, key, targetList) ?? '(無効)'
							}
						}
					} else if (optionDef.type === 'choice' && optionDef.data) {
						const data = optionDef.data.find((opt) => opt.value === option.value)
						if (data && data.showOnTitle !== false) {
							return {
								def: optionDef,
								text: data.label
							}
						}
					}
					return []
				}
			)
		) || []
	},
	[configure, options, itemData, globalAxisKey, targetList]
)

type DashBoardItemTitleProps<T> = {
	id: number
  titles: ReturnType<typeof useDashBoardItemTitles<T>>
} & Omit<StackProps, "id">
export const DashBoardItemTitle = <T,>({
	id,
	titles,
	...others
}: DashBoardItemTitleProps<T>) => {
	return (
		<Stack
			direction="row"
			alignItems="center"
			paddingY={1.5}
			whiteSpace="nowrap"
			overflow="hidden"
			textOverflow="ellipsis"
			{...others}
		>
			{titles.length === 0 ? (
				<Typography variant="body2" color="text.primary">
					(No title)
				</Typography>
			) : titles.map((word, i) => {
				const text = word.text.trim()
				if (text === '') return <></>
				return (
					<Stack direction="row" key={`dashboarditem-${id}-title-${i}`} alignItems="baseline">
						{word.def?.prefix && (
							<Typography variant="body2" color="text.secondary">
								{word.def.prefix}
							</Typography>
						)}
						<Typography variant="body2" color="text.primary">
							{text}
						</Typography>
						{word.def?.suffix && (
							<Typography variant="body2" color="text.secondary">
								{word.def.suffix}
							</Typography>
						)}
					</Stack>
				)
			})}
		</Stack>
	)
}